import { Location } from 'history';
import { RootState } from 'store';
import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';

import { ChannelTypeEnum } from 'curtain/enums/ChannelTypeEnum';
import { CurtainTabsEnum } from 'curtain/enums/CurtainTabsEnum';
import { IChannel } from 'curtain/interfaces/IChannel';
import { ICurtainTopic } from 'curtain/interfaces/ICurtainTopic';
import { TopicType } from 'curtain/enums/TopicTypeEnum';

import {
  setActiveChannel,
  setActiveEntityType,
  setActiveLineItemID,
  setActiveStyleID,
  setActiveTopic,
  setBackToChanel,
  setCurtainTab,
} from 'curtain/store/curtainActions';

import {
  INotificationDetals,
  NotificationVariationEnums,
} from 'overview/interface/Notification';

import { useAppDispatch } from './useAppDispatch';

export const useApplyNotifLocationState = () => {
  const location: Location<INotificationDetals> = useLocation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  // resetting the location state wipes the ids before the channels and topics
  // are loaded, so the notification details are kept around until we are done
  const notifDetailsRef = useRef<INotificationDetals | null>(null);

  const styleChannels = useSelector(
    (state: RootState) => state.curtainReducer.styleChannels
  );

  const lineItemChannels = useSelector(
    (state: RootState) => state.curtainReducer.lineItemChannels
  );

  const topics = useSelector((state: RootState) => state.curtainReducer.topics);

  const otherTopics = useSelector(
    (state: RootState) => state.curtainReducer.otherTopics
  );

  const resetLocationState = () => {
    if (!notifDetailsRef.current) return;

    notifDetailsRef.current = null;
    history.replace(location.pathname, { dontClearCurtain: true });
  };

  const selectColabEntity = (channel: IChannel) => {
    const isStyleChannel = channel.type === ChannelTypeEnum.STYLE;

    dispatch(setActiveChannel(channel));
    dispatch(setBackToChanel(channel));

    dispatch(
      isStyleChannel
        ? setActiveStyleID(channel.id)
        : setActiveLineItemID(channel.id)
    );

    dispatch(
      setActiveEntityType(
        isStyleChannel ? TopicType.STYLE : TopicType.LINE_ITEM
      )
    );

    // the topic of this channel still has to be opened
    if (!notifDetailsRef.current?.topicId) {
      resetLocationState();
    }
  };

  const selectColabTopic = (topic: ICurtainTopic) => {
    dispatch(setActiveTopic(topic));
    resetLocationState();
  };

  const apply = () => {
    switch (location.state.variation) {
      case NotificationVariationEnums.COMMENT_CREATED:
      case NotificationVariationEnums.DRAFTING_ATTENTIONS_NEEDED:
        // dispatch stuff for collaboration location state
        // drafting attention notifications carry only the order id, so this
        // leaves the curtain on the list of topics instead of a single topic
        dispatch(setCurtainTab(CurtainTabsEnum.COLLABORATION_TAB));
        break;
      case NotificationVariationEnums.COLLABORATOR_ADDED:
      case NotificationVariationEnums.ASSIGNEE_ADDED:
      case NotificationVariationEnums.ORDER_CANCELLATION_REQUEST:
        // nothing - user has been already redirected to basic info page of the order
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!location.state) return;

    if (location.state.redirectedFromNotif) {
      notifDetailsRef.current = location.state;
    }

    apply();
  }, [location.state]);

  // channels can already be in the store from an earlier visit, so this also
  // runs on the location state change and not only when they are (re)loaded
  useEffect(() => {
    const entityId = notifDetailsRef.current?.entityId;

    if (!entityId) return;

    const channel = [...styleChannels, ...lineItemChannels].find(
      (colabChannel: IChannel) => colabChannel.entityId === entityId
    );

    if (channel) {
      selectColabEntity(channel);
    }
  }, [location.state, styleChannels, lineItemChannels]);

  useEffect(() => {
    const topicId = notifDetailsRef.current?.topicId;

    if (!topicId) return;

    const foundTopic = [...(topics ?? []), ...(otherTopics ?? [])].find(
      (topic: ICurtainTopic) => topic.id === topicId
    );

    if (foundTopic) {
      selectColabTopic(foundTopic);
    }
  }, [location.state, topics, otherTopics]);

  return undefined;
};
